import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  TablePagination,
} from "@mui/material";
import ScheduleItem from "./ScheduleItem";
import { TypographyNormal } from "../customComponent";
import { getSchedules } from "../../store/actions/scheduleAction";
import { colorStyling } from "../../helpers/color";
import { filterObjtoString } from "../../helpers/filterHelper";
import {
  SET_LOADING_SCHEDULE,
  SET_SCHEDULE_PAGE,
  SET_SCHEDULE_ROWS_PER_PAGE,
} from "../../store/actionTypes/scheduleActionType";
import { parseVariables } from "../../helpers/localize";

const ScheduleList = () => {
  const dispatch = useDispatch();
  const {
    schedules,
    scheduleFilters,
    loadingSchedule: loading,
    page,
    rowsPerPage,
  } = useSelector((state) => state.schedule);

  const { t: tCommon } = useTranslation();

  const scrollRef = useRef(null);

  const handleLoading = (value) => {
    dispatch({ type: SET_LOADING_SCHEDULE, payload: value });
  };

  const handleChangePagination = (e, page) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    dispatch({ type: SET_SCHEDULE_PAGE, payload: page });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: SET_SCHEDULE_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
    handleChangePagination(null, 0);
  };

  const fetchData = async (filterObj) => {
    let isPrefetch = false;
    let limit = rowsPerPage;
    let offset = page * rowsPerPage;
    if (offset === 0) {
      limit = limit * 2; //fetch 2 page worth of data
      isPrefetch = false;
    } else {
      offset = (page + 1) * rowsPerPage;
      isPrefetch = true;
    }
    let filterString = filterObjtoString(filterObj, limit, offset);

    const schedulesLength = schedules?.items?.length;
    if (schedulesLength === 0) handleLoading(true);
    if (
      schedulesLength !== schedules.totalItem ||
      schedulesLength === 0 ||
      offset === 0
    )
      dispatch(getSchedules(filterString, isPrefetch, handleLoading));
  };

  useEffect(() => {
    fetchData(scheduleFilters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchData(scheduleFilters);
    } else handleChangePagination(null, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleFilters]);

  const displayedRowsLabel = ({ from, to, count }) => {
    return parseVariables(tCommon("table.displayedRowsLabel"), {
      prefix: `${from}–${to}`,
      suffix:
        count !== -1
          ? count
          : `${tCommon("table.displayedRowsMoreThan")} ${to}`,
    });
  };

  return (
    <>
      <Box>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: "20vh" }}
          >
            <CircularProgress
              size={20}
              thickness={3}
              sx={{ color: colorStyling.primary }}
            />
            <TypographyNormal
              sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
            >
              {tCommon("loadingDataList")}
            </TypographyNormal>
          </Box>
        ) : (
          <>
            {!schedules.items.length ? (
              <Paper elevation={2} sx={{ borderRadius: "6px", p: 2 }}>
                <Alert severity="info" variant="outlined">
                  <strong>{tCommon("noData")}</strong>
                </Alert>
              </Paper>
            ) : (
              <Paper
                component="section"
                elevation={2}
                sx={{ p: 2, my: 2, borderRadius: "6px" }}
              >
                <Box ref={scrollRef} sx={{ overflowY: "auto", maxHeight: 500 }}>
                  {schedules?.items
                    ?.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                    ?.map((schedule) => (
                      <ScheduleItem
                        key={schedule.id}
                        schedule={schedule}
                        dispatch={dispatch}
                      />
                    ))}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {(!loading || schedules?.totalItem > 0) && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            pr: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Paper component="div" elevation={1} sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={schedules.totalItem}
              page={page}
              onPageChange={handleChangePagination}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={displayedRowsLabel}
              labelRowsPerPage={tCommon("table.rowsPerPageLabel")}
            />
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ScheduleList;
